import styled, { css } from 'styled-components';
import { tokens } from '@veneer/tokens';

const fontBase = (size: string, lineHeight: string, weight = 400) => css`
  font-family: ${tokens.typography.family0};
  font-weight: ${weight};
  font-size: ${size};
  line-height: ${lineHeight};
  letter-spacing: 0;
`;

export const fontTitle = fontBase(
  tokens.typography.size3,
  tokens.layout.size6,
  400
);

export const fontHighlight = fontBase(
  tokens.typography.size1,
  tokens.layout.size5,
  700
);

export const fontBody = fontBase(
  tokens.typography.size1,
  tokens.layout.size5,
  400
);

export const ActionLink = styled.a`
  ${fontHighlight};
  color: #024ad8;
  outline: none;
  box-shadow: none;
  transition: none;
  position: relative;
  border-radius: 6px;
  &:hover {
    color: #0036c4;
  }
  &:active {
    color: #001cb0;
  }
  &:focus-visible {
    outline: #001cb0 solid 2px;
    outline-offset: 2px;
  }
`;

// Offer pricing styles
export const OfferPricing = styled.div`
  display: flex;
  align-items: center;
  gap: ${tokens.layout.size1};
`;

export const OfferCurrentPrice = styled.span`
  color: ${tokens.color.gray9};
`;

export const OfferPriceBefore = styled.span`
  color: ${tokens.color.gray7};
  text-decoration: line-through;
`;

// Card component styles
const CARD_BASE_HEIGHT = '152px';
const IMAGE_WIDTH = '180px';
const CHEVRON_SIZE = '36px';
const CHEVRON_BG_HOVER = '#e4eefa';

const transitionAll = css`
  transition:
    background-color 0.3s ease,
    border-color 0.3s ease,
    color 0.3s ease;
`;

const clamp = (lines: number) => css`
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${lines};
  overflow: hidden;
`;

export const Chevron = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${CHEVRON_SIZE};
  height: ${CHEVRON_SIZE};
  margin: 0 ${tokens.layout.size4};
  border-radius: ${tokens.layout.size2};
  align-self: center;
  ${transitionAll};

  svg {
    width: ${tokens.layout.size6};
    height: ${tokens.layout.size6};
  }

  @media (max-width: ${tokens.layout.xsMax}) {
    position: absolute;
    top: ${tokens.layout.size3};
    right: ${tokens.layout.size3};
    margin: 0;
    align-self: flex-start;
  }
`;

const interactiveChevronBg = (color: string) => css`
  &:hover ${Chevron}, &:focus-visible ${Chevron} {
    background-color: ${color};
  }
`;

export const Card = styled.div<{ $forcedHeight?: number }>`
  box-sizing: border-box;
  position: relative;
  display: flex;
  width: 100%;
  margin: 0;
  background: ${tokens.color.white};
  border: 1px solid #e0e0e0;
  border-radius: ${tokens.layout.size3};
  overflow: hidden;
  cursor: pointer;
  align-items: stretch;
  min-height: ${CARD_BASE_HEIGHT};
  height: ${({ $forcedHeight }) =>
    $forcedHeight ? `${$forcedHeight}px` : 'auto'};
  ${transitionAll};
  ${interactiveChevronBg(CHEVRON_BG_HOVER)};

  &:active ${Chevron} {
    background-color: #c9e0fc;
  }

  &:hover {
    border-color: ${tokens.color.gray8};
  }

  &:focus-visible {
    outline-style: solid;
    outline-width: 2px;
    outline-offset: -2px;
    outline-color: #001cb0;
  }

  @media (max-width: ${tokens.layout.xsMax}) {
    flex-direction: column;
    min-height: unset;
    height: auto;
  }
`;

export const ImageWrapper = styled.div`
  flex: 0 0 ${IMAGE_WIDTH};
  width: ${IMAGE_WIDTH};
  height: ${CARD_BASE_HEIGHT};
  position: relative;
  overflow: hidden;
  background-color: ${tokens.color.gray2};

  @media (max-width: ${tokens.layout.xsMax}) {
    width: 100%;
    flex: 0 0 auto;
    height: auto;
  }
`;

export const CardImage = styled.img<{ $isLoading: boolean }>`
  width: 100%;
  height: 100%;
  object-fit: fill;
  object-position: center;
  display: block;
  filter: ${({ $isLoading }) => ($isLoading ? 'blur(40px)' : 'blur(0)')};
  opacity: ${({ $isLoading }) => ($isLoading ? 0.5 : 1)};
  transition:
    filter 0.4s step-start,
    opacity 0.4s step-start;

  @media (max-width: ${tokens.layout.xsMax}) {
    aspect-ratio: 16 / 9;
    min-height: 200px;
    object-fit: fill;
  }
`;

export const Content = styled.div`
  flex: 1;
  min-width: 0;
  padding: ${tokens.layout.size4};
  display: flex;
  flex-direction: column;
  gap: ${tokens.layout.size2};

  @media (max-width: ${tokens.layout.xsMax}) {
    padding: ${tokens.layout.size3};
  }
`;

export const Title = styled.h2`
  ${fontTitle};
  margin: 0;
  color: ${tokens.color.gray9};
  ${clamp(1)};
`;

export const Highlight = styled(ActionLink)`
  ${fontHighlight};
  ${clamp(1)};
`;

export const Description = styled.p`
  ${fontBody};
  margin: 0;
  color: ${tokens.color.gray9};
  ${clamp(2)};

  @media (max-width: ${tokens.layout.xsMax}) {
    ${clamp(4)};
  }
`;
